import {
    ReactNode,
    InputHTMLAttributes,
    TextareaHTMLAttributes,
    useId,
    useState,
    ClipboardEvent,
    forwardRef,
} from 'react';
import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { cva } from 'class-variance-authority';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';

const wrapperVariants = cva(
    'relative flex w-full rounded-[2px] border text-xs placeholder:text-grey-300 focus-within:outline-none',
    {
        variants: {
            variant: {
                base: 'h-8 flex-row items-center px-1.5',
                area: 'min-h-[52px] flex-row items-start p-1.5',
            },
            error: {
                true: 'border-error-600 focus-within:border-error-500',
                false: 'border-grey-500 focus-within:border-grey-300',
            },
            disabled: {
                true: 'border-grey-600 bg-grey-650 text-grey-300 placeholder:text-grey-300',
                false: 'text-white-100',
            },
        },
        defaultVariants: {
            variant: 'base',
            error: false,
            disabled: false,
        },
    },
);

type BaseInputProps = {
    label?: ReactNode;
    labelClassName?: string;
    wrapperClassName?: string;
    leadingIcon?: ReactNode;
    leadingText?: string;
    trailingIcon?: ReactNode;
    error?: string;
    className?: string;
};

type TextFieldBaseProps = BaseInputProps &
    Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> & {
        type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'number';
        variant?: 'base';
        onPaste?: (event: ClipboardEvent<HTMLInputElement>) => void;
    };

type TextFieldAreaProps = BaseInputProps &
    Omit<
        TextareaHTMLAttributes<HTMLTextAreaElement>,
        'className' | 'onPaste'
    > & {
        variant: 'area';
    };

export type TextFieldProps = TextFieldBaseProps | TextFieldAreaProps;

function isAreaProps(props: TextFieldProps): props is TextFieldAreaProps {
    return props.variant === 'area';
}

export const TextField = forwardRef<
    HTMLInputElement | HTMLTextAreaElement,
    TextFieldProps
>(function TextField(props, ref) {
    const {
        label,
        labelClassName,
        wrapperClassName,
        leadingIcon,
        leadingText,
        trailingIcon,
        className,
        error,
        disabled = false,
        required,
        id: idProp,
        variant: _variant,
        ...rest
    } = props;

    const internalId = useId();
    const fieldId = idProp ?? (label ? internalId : undefined);

    const isArea = isAreaProps(props);
    const type = !isArea
        ? ((rest as TextFieldBaseProps).type ?? 'text')
        : undefined;
    const [showPassword, setShowPassword] = useState(false);
    const isPasswordField = type === 'password';
    const actualInputType = isPasswordField && showPassword ? 'text' : type;
    const hasRightPadding = Boolean(trailingIcon || isPasswordField);

    const {
        type: _type,
        onPaste,
        ...inputRest
    } = !isArea ? (rest as TextFieldBaseProps) : ({} as TextFieldBaseProps);

    const hasLeadingIcon = Boolean(leadingIcon);
    const hasLeadingText = Boolean(leadingText);
    const wrapperGap = hasLeadingText && !hasLeadingIcon ? 'gap-0' : 'gap-1.5';

    const inputClasses = cn(
        'min-w-0 flex-1 border-0 bg-transparent p-0 outline-none placeholder:text-grey-300 disabled:text-grey-300 disabled:placeholder:text-grey-300',
        hasRightPadding && 'pr-7',
    );

    const content = (
        <>
            {(leadingIcon || leadingText) && (
                <div
                    className={cn(
                        'flex flex-shrink-0 items-center pointer-events-none',
                        hasLeadingIcon && hasLeadingText && 'gap-2',
                    )}
                >
                    {leadingIcon && (
                        <span className="flex size-3.5 items-center justify-center text-grey-300 [&>svg]:size-3.5">
                            {leadingIcon}
                        </span>
                    )}
                    {leadingText && (
                        <span className="select-none text-xs text-grey-100">
                            {leadingText}
                        </span>
                    )}
                </div>
            )}
            {isArea ? (
                <textarea
                    ref={ref as React.Ref<HTMLTextAreaElement>}
                    id={fieldId}
                    disabled={disabled}
                    className={cn(inputClasses, 'min-h-[40px] resize-none')}
                    {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
                />
            ) : (
                <input
                    ref={ref as React.Ref<HTMLInputElement>}
                    id={fieldId}
                    type={actualInputType}
                    disabled={disabled}
                    onPaste={onPaste}
                    className={inputClasses}
                    {...inputRest}
                />
            )}
            {!isArea && isPasswordField && (
                <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 flex-shrink-0 text-grey-300 hover:text-grey-200 transition-colors cursor-pointer"
                    tabIndex={-1}
                    aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                    }
                >
                    {showPassword ? (
                        <EyeSlashIcon className="size-3.5" />
                    ) : (
                        <EyeIcon className="size-3.5" />
                    )}
                </button>
            )}
            {!isArea && !isPasswordField && trailingIcon && (
                <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex-shrink-0 pointer-events-none">
                    {trailingIcon}
                </div>
            )}
        </>
    );

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            {label && (
                <Label
                    htmlFor={fieldId}
                    required={Boolean(required)}
                    className={labelClassName}
                >
                    {label}
                </Label>
            )}
            <div
                className={cn(
                    wrapperVariants({
                        variant: isArea ? 'area' : 'base',
                        error: Boolean(error),
                        disabled,
                    }),
                    wrapperClassName,
                    wrapperGap,
                )}
            >
                {content}
            </div>
            {error && <p className="text-xs text-error-500">{error}</p>}
        </div>
    );
});

TextField.displayName = 'TextField';
